import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { logger } from "../../../utils/logger";
import { WELCOME_MESSAGES, WELCOME_OPTIONS } from '../constants';

/**
 * Build a proactive assistant greeting for guided workflows.
 * Instead of "What can I help with?", the AI says
 * "I see you have X sessions today, let's get them documented."
 */
interface GuidedGreeting {
  text: string;
  options?: Array<{ label: string; value: string }>;
}

export function buildGuidedGreeting(guidedMode: string, data: Record<string, unknown>): GuidedGreeting | null {
  const totalCount = (data.total_count as number) || 0;
  if (totalCount === 0) return null;

  // When guided_today had 0 appointments and fell back to guided_pending,
  // the data includes _fallbackFromToday so we can craft a transition message.
  const isFallback = Boolean(data._fallbackFromToday);

  const isResumed = Boolean(data._resumedFromDb);
  const completedCount = (data.completed_count as number) || 0;
  const remaining = Math.max(0, totalCount - completedCount);

  if (isResumed) {
    const unit = guidedMode === 'guided_pending' ? 'note' : 'appointment';
    const text = `Welcome back! What would you like to do?`;
    const resumeLabel = `Pick up where you left off (${remaining} pending ${unit}${remaining !== 1 ? 's' : ''} remaining)`;
    return {
      text,
      options: [
        { label: resumeLabel, value: resumeLabel },
        ...WELCOME_OPTIONS,
      ],
    };
  }

  if (guidedMode === 'guided_pending') {
    const notes = (data.notes as Array<{ patient_name?: string; sessions?: Array<{ session_type?: string; date?: string; time?: string }> }>) || [];
    const firstNote = notes[0];
    const firstSession = firstNote?.sessions?.[0];
    const intro = isFallback
      ? `No new appointments to document today, but you have ${totalCount} pending note${totalCount !== 1 ? 's' : ''} to catch up on!`
      : `Welcome to Catch Up with AI! You have ${totalCount} session${totalCount !== 1 ? 's' : ''} with pending notes.`;
    if (firstSession && firstNote) {
      return { text: `${intro} I'll walk you through each one. First up is <strong>${firstNote.patient_name}</strong> ${firstSession.session_type || ''} on ${firstSession.date}${firstSession.time ? ' at ' + firstSession.time : ''}. If you want to start with someone else, just click their name in the schedule.` };
    }
    return { text: `${intro} I'll walk you through each one so we can document them all easily.` };
  }

  // guided_today
  const appointments = (data.appointments as Array<{ patient_name?: string; session_type?: string; time?: string }>) || [];
  const firstAppt = appointments[0];
  if (firstAppt) {
    return { text: `Welcome to Write Today's Notes with AI! I'll walk you through each note and we can document them all easily in one conversation. First up is <strong>${firstAppt.patient_name}</strong> ${firstAppt.session_type || ''} at ${firstAppt.time}. If you want to start with someone else, just click their name in the schedule.` };
  }
  return { text: `Welcome to Write Today's Notes with AI! You have ${totalCount} appointment${totalCount !== 1 ? 's' : ''} today. I'll walk you through each one so we can document them all easily.` };
}

/**
 * Try to set the guided greeting immediately if workflow data is available.
 * Returns true if greeting was set, false if data isn't ready yet.
 * (Caller should let the guided-greeting useEffect handle it when data arrives.)
 */
export function trySetGuidedGreeting(
  effectiveGuidedMode: string | null,
  guidedWorkflowData: Record<string, unknown> | null,
  setMessages: (msgs: unknown) => void,
  logPrefix: string
): boolean {
  if (!effectiveGuidedMode || !guidedWorkflowData) return false;

  const greeting = buildGuidedGreeting(effectiveGuidedMode, guidedWorkflowData);
  if (greeting) {
    logger.log(`🎯 ${logPrefix} — setting greeting directly after new session`);
    setMessages([{ sender: 'assistant', text: greeting.text, ...(greeting.options ? { options: greeting.options } : {}) }]);
    return true;
  }
  // totalCount === 0: return false so caller falls through to generic welcome
  return false;
}

/**
 * Session lifecycle management hook.
 *
 * ROOT-CAUSE FIX (March 2026): The previous implementation used useRef guards
 * (sessionCreatedThisMount, guidedNewSessionStarted, initialGuidedPromptSent)
 * that reset on every TherapistPanel remount (tab switch).  This meant the hook
 * could NOT distinguish "resume THIS guided session" from "create a NEW guided
 * session for this mode" after a tab switch — causing the conversation to reset.
 *
 * The fix: guided-session metadata is stored in ConversationContext (persists
 * across tab switches) AND backed by sessionStorage (persists across page
 * reloads).  The hook reads this persistent state to make the correct decision.
 *
 * Removed refs:
 *   - sessionCreatedThisMount  → replaced by getGuidedSessionInfo()
 *   - guidedNewSessionStarted  → replaced by getGuidedSessionInfo()
 *   - initialGuidedPromptSent  → replaced by getGuidedPromptSentForSession()
 *
 * Kept refs (per-mount lifecycle, intentionally reset on remount):
 *   - sessionCreationFailed    → prevents infinite retry loops
 *   - isCreatingSession        → prevents duplicate concurrent creation
 *   - hasShownResumeToast      → prevents duplicate toasts within a mount
 *   - hasForceRefetched        → prevents duplicate message re-fetches within a mount
 */
export function useSessionManagement({
  user,
  currentSessionId,
  startNewConversation,
  rehydrating,
  contextLoading,
  conversations,
  hasFetched,
  loadConversationMessages,
  setMessages,
  setCurrentPatient,
  setCurrentEncounter,
  guidedMode,
  guidedWorkflowData,
  sendMessage,
  // Persistent guided-session state (lives on ConversationContext, survives tab switches + page reloads)
  getGuidedSessionInfo,
  setGuidedSessionInfo,
  clearGuidedSessionInfo,
  getGuidedPromptSentForSession,
  setGuidedPromptSentForSession,
  clearGuidedPromptSentForSession
}) {
  const sessionCreationFailed = useRef(false);
  const isCreatingSession = useRef(false);
  const hasShownResumeToast = useRef(false);
  // Track whether the force re-fetch for tab-switch remount has already
  // fired for this mount.  Re-initialises on each remount (component
  // unmounts → ref destroyed → fresh useRef on mount).
  //
  // Key distinction: on COLD START (fresh app load), hasFetched.current
  // starts as false — useMessageLoading's own effect handles the initial
  // fetch, so we initialise this to true to prevent the force branch from
  // also firing (which would double-fetch).  On TAB-SWITCH REMOUNT,
  // hasFetched.current is already true (context persists across mounts),
  // so we initialise to false — allowing the force re-fetch to fire once.
  const hasForceRefetched = useRef(!hasFetched.current);

  useEffect(() => {
    if (user === null || !user) return;

    const isAssistantRoute = window.location.pathname === '/ai-assistant' ||
                            window.location.pathname === '/assistant' ||
                            window.location.pathname === '/dashboard' ||
                            window.location.pathname.startsWith('/conversations');

    if (!isAssistantRoute) {
      logger.log("🚫 Skipping assistant initialization on route:", window.location.pathname);
      return;
    }

    if (rehydrating || contextLoading) {
      logger.log("[SESSION_REHYDRATE_DEBUG] waiting_for_rehydrate rehydrating=", rehydrating, " loading=", contextLoading);
      return;
    }

    // Detect guided mode from URL params (React state may not have propagated yet).
    const urlParams = new URLSearchParams(window.location.search);
    const urlMode = urlParams.get('mode');
    const isGuidedUrl = urlMode === 'guided_pending' || urlMode === 'guided_today';

    // ── CASE 1: We already have a session ──────────────────────────────
    if (currentSessionId) {

      if (isGuidedUrl) {
        // Use PERSISTENT state to decide: resume vs create new.
        // This is the core fix — survives tab switches and page reloads.
        const info = getGuidedSessionInfo();
        const isResumingGuidedSession = info?.sessionId === currentSessionId && info?.mode === urlMode;

        if (isResumingGuidedSession) {
          // ── CASE 1a: Same guided session, just tab-switched → RESUME ──
          logger.log("🔄 Resuming guided session (tab-switch):", currentSessionId, "mode:", urlMode);
          if (!hasFetched.current) {
            loadConversationMessages(currentSessionId);
          } else if (!hasForceRefetched.current) {
            hasForceRefetched.current = true;
            logger.log("🔄 Force re-fetching messages for tab-switch remount");
            loadConversationMessages(currentSessionId, 1, true, true);
          }
          return;
        }

        // ── CASE 1b: Guided URL but different/old session → CREATE NEW ──
        if (isCreatingSession.current) {
          logger.log("🚫 Guided session creation already in progress");
          return;
        }
        logger.log("🔄 Guided mode detected with existing non-guided session — starting new conversation");
        isCreatingSession.current = true;
        hasForceRefetched.current = true; // Prevent force re-fetch of old session
        setCurrentPatient(null);
        setCurrentEncounter(null);
        startNewConversation().then((newId) => {
          isCreatingSession.current = false;
          if (!newId) {
            logger.error("❌ Failed to create guided session");
            sessionCreationFailed.current = true;
            toast.error("Failed to start a new conversation. Please try refreshing the page.");
            return;
          }
          logger.log("✅ New guided session started:", newId);
          sessionCreationFailed.current = false;
          hasFetched.current = true;
          hasForceRefetched.current = true;
          // Record in persistent state so tab-switch remounts know to RESUME
          setGuidedSessionInfo(newId, urlMode);
          const effectiveGuidedMode = guidedMode || urlMode;
          const greetingSet = trySetGuidedGreeting(effectiveGuidedMode, guidedWorkflowData, setMessages, "Guided (existing session)");
          if (!greetingSet) {
            // Data not loaded yet — the guided greeting useEffect will handle it.
            // Safety: if data doesn't arrive within 5s, show generic welcome.
            setTimeout(() => {
              setMessages((prev: Array<{ sender: string; text?: string; options?: unknown }>) => {
                if (prev.length > 0) return prev;
                const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
                return [{ sender: 'assistant', text: welcome, options: WELCOME_OPTIONS }];
              });
            }, 5000);
          }
          toast.info("🆕 New conversation started");
        }).catch((error) => {
          isCreatingSession.current = false;
          logger.error("❌ Guided session creation error:", error);
          sessionCreationFailed.current = true;
          toast.error("Failed to start a new conversation. Please try refreshing the page.");
        });
        return;
      }

      // ── CASE 1c: Not guided URL — normal resume ──────────────────────
      const activeConv = conversations.find(c => c.id === currentSessionId);
      const title = activeConv?.title || "Untitled";
      logger.log("🔄 Resuming session:", currentSessionId);
      
      if (!hasShownResumeToast.current) {
        toast.info(`🔁 Resumed "${title}" conversation`);
        hasShownResumeToast.current = true;
      }

      if (!hasFetched.current) {
        logger.log("📥 TherapistPanel: Loading messages for resumed session");
        loadConversationMessages(currentSessionId);
      } else if (!hasForceRefetched.current) {
        hasForceRefetched.current = true;
        logger.log("🔄 TherapistPanel: Force re-fetching messages for tab-switch remount");
        loadConversationMessages(currentSessionId, 1, true, true);
      }
      return;
    }

    hasShownResumeToast.current = false;
    
    if (sessionCreationFailed.current) {
      logger.log("🚫 Session creation previously failed, not retrying to prevent infinite loop");
      return;
    }
    
    if (isCreatingSession.current) {
      logger.log("🚫 Session creation already in progress, preventing duplicate");
      return;
    }
    
    logger.log("🧪 No current session ID, creating new session");
    isCreatingSession.current = true;
    
    setCurrentPatient(null);
    setCurrentEncounter(null);
    
    startNewConversation().then((newId) => {
      isCreatingSession.current = false;
      if (!newId) {
        logger.error("❌ Failed to create new session");
        sessionCreationFailed.current = true;
        toast.error("Failed to start a new conversation. Please try refreshing the page.");
        return;
      }

      logger.log("✅ New session started:", newId);
      sessionCreationFailed.current = false;
      hasFetched.current = true;

      // Detect guided mode from URL params as well as React state.
      const params = new URLSearchParams(window.location.search);
      const freshUrlMode = params.get('mode');
      const effectiveGuidedMode = guidedMode || ((freshUrlMode === 'guided_pending' || freshUrlMode === 'guided_today') ? freshUrlMode : null);

      if (effectiveGuidedMode) {
        // Record in persistent state
        setGuidedSessionInfo(newId, effectiveGuidedMode);
        const greetingSet = trySetGuidedGreeting(effectiveGuidedMode, guidedWorkflowData, setMessages, "Guided (no session)");
        if (!greetingSet) {
          // Data not loaded yet — guided greeting useEffect will handle it.
          setTimeout(() => {
            setMessages((prev: Array<{ sender: string; text?: string; options?: unknown }>) => {
              if (prev.length > 0) return prev;
              const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
              return [{ sender: 'assistant', text: welcome, options: WELCOME_OPTIONS }];
            });
          }, 5000);
        }
      } else {
        // Not guided — clear any stale guided state and show generic welcome
        clearGuidedSessionInfo();
        clearGuidedPromptSentForSession();
        const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
        setMessages([{ sender: 'assistant', text: welcome, options: WELCOME_OPTIONS }]);
      }
      toast.info("🆕 New conversation started");
    }).catch((error) => {
      isCreatingSession.current = false;
      logger.error("❌ Session creation error:", error);
      sessionCreationFailed.current = true;
    });
  }, [user, currentSessionId, startNewConversation, rehydrating, contextLoading, conversations, hasFetched, loadConversationMessages, setMessages, setCurrentPatient, setCurrentEncounter, guidedMode, guidedWorkflowData, getGuidedSessionInfo, setGuidedSessionInfo, clearGuidedSessionInfo, clearGuidedPromptSentForSession]);

  // ── Guided greeting + prompt effect ──────────────────────────────────
  // Fires when guidedMode + guidedWorkflowData + currentSessionId are all
  // available.  Uses the PERSISTENT getGuidedPromptSentForSession() to
  // prevent re-sending on tab-switch remounts.  The old
  // initialGuidedPromptSent ref would reset on every remount, causing
  // duplicate prompts — this is the root-cause fix.
  useEffect(() => {
    if (!guidedMode || !guidedWorkflowData || !currentSessionId) return;

    const totalCount = (guidedWorkflowData.total_count as number) || 0;

    // Set the contextual greeting as the first assistant message.
    const greeting = buildGuidedGreeting(guidedMode, guidedWorkflowData);
    if (greeting) {
      logger.log("🎯 Setting guided greeting:", greeting.text.substring(0, 60) + "...");
      setMessages(prev => {
        // Only set if the greeting isn't already there
        const hasGreeting = prev.some(m => m.sender === 'assistant' && (m.text?.includes('Welcome to Catch Up') || m.text?.includes('Welcome to Write Today') || m.text?.includes("Pick up where you left off") || m.text?.includes('No new appointments') || m.text?.includes('What would you like to do?')));
        if (hasGreeting) return prev;
        // Replace any generic welcome messages with the contextual greeting
        const filtered = prev.filter(m => !(m.sender === 'assistant' && m.options));
        return [{ sender: 'assistant', text: greeting.text, ...(greeting.options ? { options: greeting.options } : {}) }, ...filtered];
      });
    }

    const encounters = guidedMode === 'guided_pending'
      ? (guidedWorkflowData.notes || []).flatMap(group =>
          (group.sessions || []).map(session => ({
            encounter_id: session.encounter_id,
            patient_id: group.patient_id,
            patient_name: group.patient_name,
            date: session.date,
            time: session.time,
            session_type: session.session_type,
            status: session.status,
            default_template_id: session.default_template_id,
            default_template_name: session.default_template_name
          }))
        )
      : (guidedWorkflowData.appointments || []).map(apt => ({
          encounter_id: apt.encounter_id,
          patient_id: apt.patient_id,
          patient_name: apt.patient_name,
          time: apt.time,
          session_type: apt.session_type,
          status: apt.status,
          default_template_id: apt.default_template_id,
          default_template_name: apt.default_template_name
        }));

    const firstEncounter = encounters[0];
    if (firstEncounter?.patient_id && firstEncounter?.patient_name) {
      setCurrentPatient(prev => prev || {
        id: firstEncounter.patient_id,
        name: firstEncounter.patient_name,
      });
      setCurrentEncounter(prev => prev || { id: firstEncounter.encounter_id });
    }

    const alreadySent = getGuidedPromptSentForSession();
    if (alreadySent === currentSessionId) return;

    // If there are no items, don't send a confusing "0 appointments" prompt.
    if (totalCount === 0) {
      setGuidedPromptSentForSession(currentSessionId);
      return;
    }

    // For resumed workflows, show options and wait for user to choose.
    // The backend _resume_from_db() will handle the workflow context when
    // the user sends their first message.
    const isResumed = Boolean(guidedWorkflowData._resumedFromDb);
    if (isResumed) {
      setGuidedPromptSentForSession(currentSessionId);
      return;
    }

    const prompt = guidedMode === 'guided_pending'
      ? `I want to catch up on my notes. I have ${totalCount} sessions with pending notes.`
      : `I want to write my notes for today. I have ${totalCount} appointments today.`;

    const guidedContext = {
      mode: guidedMode,
      encounters: encounters
    };

    // Mark as sent in PERSISTENT state (survives tab switches + page reloads)
    setGuidedPromptSentForSession(currentSessionId);
    sendMessage(prompt, null, guidedContext);
  }, [guidedMode, guidedWorkflowData, currentSessionId, sendMessage, setMessages, setCurrentPatient, setCurrentEncounter, getGuidedPromptSentForSession, setGuidedPromptSentForSession]);

  return {
    sessionCreationFailed,
    isCreatingSession,
    hasShownResumeToast,
  };
}
